import { useEffect, useRef } from "react";

import { useAgent } from "@/canvas/agent/TldrawAgentAppProvider";
import { WHITEBOARD_SCAN_EVENT } from "@/canvas/components/WhiteboardScan";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";

import { registerCanvasTextSender } from "../canvasTextDelivery";

import {
  selectIsAIActiveOnCurrentPage,
  useNotepadStore,
} from "../store/useNotepadStore";

export function CanvasChatBridge() {
  const agent = useAgent();
  const isAIActive = useNotepadStore(selectIsAIActiveOnCurrentPage);
  const isAIActiveRef = useRef(isAIActive);
  isAIActiveRef.current = isAIActive;

  useEffect(() => {
    if (!agent) return;

    // Registered rather than subscribed: this component mounts only once
    // tldraw's onMount has produced an app, while the send path's gates are
    // satisfied by the WS transport alone. A bus emit into that window is a
    // silent no-op, so the send would clear the student's text for a message
    // nothing had accepted. The boolean is what the send reports.
    return registerCanvasTextSender((raw: string): boolean => {
      if (!isAIActiveRef.current) return false;
      const message = (raw ?? "").trim();
      if (!message) return false;
      if (!useTurnStore.getState().getTurnId()) return false;

      if (agent.requests.hasViewportChanged()) {
        window.dispatchEvent(new CustomEvent(WHITEBOARD_SCAN_EVENT));
      }

      const currentMode = agent.mode.getCurrentModeType();
      if (currentMode === "working") {
        agent.interrupt();
      } else {
        agent.interrupt({ mode: "working" });
      }

      try {
        agent.cursor.showThinking();
      } catch {
        // Agent/editor may already be disposed if the canvas is closing.
      }

      void agent
        .request({
          source: "user",
          agentMessages: [message],
          userMessages: [message],
          bounds: agent.editor.getViewportPageBounds(),
        })
        .catch((error: unknown) => {
          console.error("[CanvasChatBridge] typed request failed:", error);
        });

      return true;
    });
  }, [agent]);

  return null;
}
